import { assert } from "@msys/common";
import { Autocomplete, TextField, TextFieldProps } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { throttle } from "lodash-es";
import React from "react";
import { useGoogleMapsApi } from "../google-maps/useGoogleMapsApi.js";

export type PlaceWithAddressComponentsAndLocation = PlaceWithAddressComponents &
  PlaceWithLocation;

interface GoogleMapsSuggestionOption {
  text: string;
  place: PlaceWithAddressComponentsAndLocation;
}
interface PassedOption<Value> {
  label: string;
  value: Value;
}

interface Props<
  Value,
  IPassedOption extends PassedOption<Value> = PassedOption<Value>,
> {
  id?: string;
  autoFocus?: boolean;
  required?: boolean;
  disabled?: boolean;
  label: string;
  initialValue?: string | null;
  onChange: (value: IPassedOption | GoogleMapsSuggestionOption | null) => void;
  error?: String;
  variant?: TextFieldProps["variant"];
  setInputValueRef?: React.MutableRefObject<
    ((string: string) => void) | undefined
  >;
  options?: IPassedOption[];
}

export const GoogleAddressSearch = <
  Value,
  IPassedOption extends PassedOption<Value> = PassedOption<Value>,
>({
  id,
  setInputValueRef,
  autoFocus = false,
  disabled,
  required,
  label,
  initialValue,
  onChange,
  error,
  variant,
  options: passedOptions = [],
}: Props<Value, IPassedOption>) => {
  const { t } = useTranslate(["Global"]);

  const { inputValue, setInputValue, setHasChanged } = useInputValue(
    initialValue ?? "",
    setInputValueRef
  );
  const { suggestions } = useGoogleMapsAutocomplete(inputValue);

  return (
    <Autocomplete<GoogleMapSuggestion | IPassedOption, false, boolean, true>
      id={id}
      autoComplete
      autoHighlight
      openOnFocus
      selectOnFocus
      freeSolo
      disabled={disabled}
      options={inputValue ? suggestions : passedOptions}
      filterOptions={option => option}
      getOptionLabel={option => {
        if (typeof option === "string") return option;
        return option.label;
      }}
      noOptionsText={t("No existing entry", { ns: "Global" })}
      value={inputValue}
      onChange={async (event, value, reason) => {
        event.stopPropagation();
        if (value === null) return;
        if (typeof value === "string") return;
        setHasChanged();

        if ("value" in value) {
          onChange(value);
        } else {
          onChange(
            value ? { text: value.label, place: await value.getPlace() } : null
          );
        }
      }}
      renderInput={params => {
        return (
          <TextField
            {...params}
            label={label}
            variant={variant}
            autoFocus={autoFocus}
            required={required}
            error={Boolean(error)}
            helperText={error}
          />
        );
      }}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "clear") {
          setInputValue("");
          onChange(null);
        }
        if (reason === "input") {
          setInputValue(newInputValue);
        }
      }}
    />
  );
};

function useInputValue(
  passedInputValue: string,
  setInputValueRef?: React.MutableRefObject<
    ((string: string) => void) | undefined
  >
) {
  const hasChangedRef = React.useRef<boolean>(false);
  const setHasChanged = React.useCallback(() => {
    hasChangedRef.current = true;
  }, []);

  const [inputValue, setInputValue] = React.useState<string>(
    passedInputValue ?? ""
  );

  React.useImperativeHandle(setInputValueRef, () => {
    return setInputValue;
  }, [setInputValue]);

  React.useEffect(() => {
    if (hasChangedRef.current) return;
    setInputValue(passedInputValue);
  }, [passedInputValue]);

  return { inputValue, setInputValue, setHasChanged };
}

interface GoogleMapSuggestion {
  label: string;
  getPlace: () => Promise<PlaceWithAddressComponentsAndLocation>;
}

function useGoogleMapsAutocomplete(inputValue: string) {
  const [suggestions, setSuggestions] = React.useState<GoogleMapSuggestion[]>(
    []
  );
  const { isLoaded } = useGoogleMapsApi();
  const sessionToken = React.useMemo(
    () => (isLoaded ? new google.maps.places.AutocompleteSessionToken() : null),
    [isLoaded]
  );

  const throttledGetPlacePredictions = React.useMemo(
    () =>
      throttle(async (request: google.maps.places.AutocompleteRequest) => {
        const result =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            request
          );

        if (result) {
          const suggestions = result.suggestions.reduce<GoogleMapSuggestion[]>(
            (acc, suggestion) => {
              if (suggestion.placePrediction) {
                return [
                  ...acc,
                  {
                    label: suggestion.placePrediction.text.text,
                    getPlace: async () => {
                      assert(
                        suggestion.placePrediction,
                        "PlacePrediction is null"
                      );
                      const { place } = (await suggestion.placePrediction
                        .toPlace()
                        .fetchFields({
                          fields: ["addressComponents", "location"],
                        })) as { place: PlaceWithAddressComponentsAndLocation };
                      assert(
                        "addressComponents" in place,
                        "No addressComponents in place"
                      );
                      assert("location" in place, "No location in place");

                      return place;
                    },
                  },
                ];
              }
              return acc;
            },
            []
          );
          setSuggestions(suggestions);
        }
      }, 200),
    []
  );

  React.useEffect(() => {
    if (inputValue === "") {
      setSuggestions([]);
      return;
    }

    if (!isLoaded || !sessionToken) return;

    throttledGetPlacePredictions({
      input: inputValue,
      // includedRegionCodes: ["de", "at", "ch", "be", "nl", "fr", "pl"],
      // region: "de",
      locationBias: new google.maps.LatLng(51.1657, 10.4515), // Center of Germany
      includedPrimaryTypes: [
        "street_address",
        "route",
        // "political",
        "locality",
        "sublocality",
        "postal_code",
        // "neighborhood",
      ],
      sessionToken,
    });

    return () => {
      throttledGetPlacePredictions.cancel();
    };
  }, [inputValue, throttledGetPlacePredictions, sessionToken, isLoaded]);

  return { suggestions };
}

type PlaceWithAddressComponents = google.maps.places.Place & {
  addressComponents: google.maps.places.AddressComponent[];
};
export function getAddressInfoFromPlace(
  place: PlaceWithAddressComponents,
  short: boolean = false
) {
  const addressComponentsMap = place.addressComponents.reduce<
    Record<string, google.maps.places.AddressComponent>
  >((acc, component) => {
    return {
      ...acc,
      ...component.types.reduce((a, type) => ({ ...a, [type]: component }), {}),
    };
  }, {});

  const route = getAddressComponentValue(addressComponentsMap, "route", short);
  const streetNumber = getAddressComponentValue(
    addressComponentsMap,
    "street_number",
    short
  );
  const postalCode = getAddressComponentValue(
    addressComponentsMap,
    "postal_code",
    short
  );
  const city = getAddressComponentValue(
    addressComponentsMap,
    "locality",
    short
  );
  const countryCode = getAddressComponentValue(
    addressComponentsMap,
    "country",
    true
  );

  const address = {
    city,
    countryCode,
    postalCode,
    streetName: route,
    streetNumber,
    streetLines1: `${route} ${streetNumber}`.trim(),
  };

  return address;
}

const getAddressComponentValue = (
  addressComponentsMap: Record<string, google.maps.places.AddressComponent>,
  key: string,
  short?: boolean
) => {
  const component = addressComponentsMap[key];

  if (component) {
    return short ? component.shortText : component.longText;
  } else {
    return "";
  }
};

type PlaceWithLocation = google.maps.places.Place & {
  location: google.maps.LatLng;
};
export function getLocationFromPlace(place: PlaceWithLocation) {
  return place.location
    ? { lat: place.location.lat(), lng: place.location.lng() }
    : null;
}
